import { useState, useEffect, useRef } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { format } from 'date-fns';
import { X, Settings, Trash2, ChevronDown, ChevronRight, Play, Pause, AlertTriangle, Archive, ArchiveRestore } from 'lucide-react';
import ImageUpload from '../../components/ImageUpload';

interface LayoutContext {
  viewAsStoreId?: string;
  viewAsStoreName?: string;
}

interface CampaignOccurrence {
  id: string;
  instanceNumber: number;
  status: string;
  startDate: string;
  endDate: string;
  numberOfWinners: number | null;
  _count?: {
    participations: number;
  };
}

interface Campaign {
  id: string;
  name: string;
  description: string | null;
  imageUrl?: string | null;
  isArchived: boolean;
  numberOfWinners: number;
  runs: CampaignOccurrence[];
  _count: {
    runs: number;
  };
}

interface CreateCampaignForm {
  name: string;
  description: string;
  numberOfWinners: number;
}

interface CreateOccurrenceForm {
  startDate: string;
  endDate: string;
  numberOfWinners: number;
  storeIds: string[];
}

interface Store {
  id: string;
  name: string;
  status: string;
}

// Status button with popover
function StatusButton({
  occurrence,
  onStatusChange
}: {
  occurrence: CampaignOccurrence;
  onStatusChange: (occurrenceId: string, newStatus: string) => void;
}) {
  const [showPopover, setShowPopover] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return {
          icon: Play,
          bgColor: 'bg-green-100',
          iconColor: 'text-green-600',
          hoverBg: 'hover:bg-green-200',
          popoverText: 'Running. Click to pause.',
          nextStatus: 'PAUSED',
          filled: true,
        };
      case 'PAUSED':
        return {
          icon: Pause,
          bgColor: 'bg-orange-100',
          iconColor: 'text-orange-600',
          hoverBg: 'hover:bg-orange-200',
          popoverText: 'Paused. Click to resume.',
          nextStatus: 'ACTIVE',
          filled: true,
        };
      case 'DRAFT':
      default:
        return {
          icon: Play,
          bgColor: 'bg-gray-100',
          iconColor: 'text-gray-400',
          hoverBg: 'hover:bg-gray-200',
          popoverText: 'Draft. Click to activate.',
          nextStatus: 'ACTIVE',
          filled: false,
        };
    }
  };

  const config = getStatusConfig(occurrence.status);
  const Icon = config.icon;

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={() => onStatusChange(occurrence.id, config.nextStatus)}
        onMouseEnter={() => setShowPopover(true)}
        onMouseLeave={() => setShowPopover(false)}
        className={`w-8 h-8 rounded-full flex items-center justify-center ${config.bgColor} ${config.hoverBg} transition-colors`}
      >
        <Icon
          className={`w-4 h-4 ${config.iconColor}`}
          fill={config.filled ? 'currentColor' : 'none'}
        />
      </button>

      {showPopover && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-nowrap z-50">
          {config.popoverText}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
        </div>
      )}
    </div>
  );
}

export default function CampaignsPage() {
  const navigate = useNavigate();
  const { viewAsStoreId } = useOutletContext<LayoutContext>() || {};
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'campaigns' | 'archive'>('campaigns');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showOccurrenceModal, setShowOccurrenceModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPauseWarning, setShowPauseWarning] = useState(false);
  const [showArchiveWarning, setShowArchiveWarning] = useState(false);
  const [pendingArchive, setPendingArchive] = useState<Campaign | null>(null);
  const [showDeleteOccurrenceModal, setShowDeleteOccurrenceModal] = useState(false);
  const [pendingDeleteOccurrence, setPendingDeleteOccurrence] = useState<{ campaignId: string; occurrence: CampaignOccurrence } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{ occurrenceId: string; newStatus: string } | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [campaignForm, setCampaignForm] = useState<CreateCampaignForm>({
    name: '',
    description: '',
    numberOfWinners: 1,
  });

  const [occurrenceForm, setOccurrenceForm] = useState<CreateOccurrenceForm>({
    startDate: '',
    endDate: '',
    numberOfWinners: 1,
    storeIds: [],
  });

  useEffect(() => {
    loadCampaigns();
  }, [viewAsStoreId]);

  const loadCampaigns = async () => {
    setLoading(true);
    setCampaigns([]);
    setExpandedCampaigns(new Set());

    try {
      const response = await apiClient.get<{ success: boolean; data: Campaign[] }>('/admin/campaigns');
      setCampaigns(response.data.data);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStores = async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: Store[] }>('/admin/stores');
      setStores(response.data.data.filter(s => s.status === 'ACTIVE'));
    } catch (error) {
      console.error('Failed to load stores:', error);
    }
  };

  const toggleExpand = (campaignId: string) => {
    const newExpanded = new Set(expandedCampaigns);
    if (newExpanded.has(campaignId)) {
      newExpanded.delete(campaignId);
    } else {
      newExpanded.add(campaignId);
    }
    setExpandedCampaigns(newExpanded);
  };

  const handleCreateCampaign = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiClient.post('/admin/campaigns', campaignForm);
      setShowCreateModal(false);
      setCampaignForm({ name: '', description: '', numberOfWinners: 1 });
      loadCampaigns();
    } catch (error: any) {
      console.error('Failed to create campaign:', error);
      setError(error.message || 'Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateOccurrence = async () => {
    if (!selectedCampaign) return;
    setSaving(true);
    try {
      await apiClient.post(`/admin/campaigns/${selectedCampaign.id}/runs`, occurrenceForm);
      setShowOccurrenceModal(false);
      setOccurrenceForm({ startDate: '', endDate: '', numberOfWinners: selectedCampaign.numberOfWinners, storeIds: [] });
      loadCampaigns();
    } catch (error) {
      console.error('Failed to create occurrence:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (occurrenceId: string, newStatus: string) => {
    // Find the current occurrence to check its status
    let currentStatus = '';
    for (const campaign of campaigns) {
      const occ = campaign.runs.find(r => r.id === occurrenceId);
      if (occ) {
        currentStatus = occ.status;
        break;
      }
    }

    // If going from ACTIVE to PAUSED, show warning
    if (currentStatus === 'ACTIVE' && newStatus === 'PAUSED') {
      setPendingStatusChange({ occurrenceId, newStatus });
      setShowPauseWarning(true);
    } else {
      // Execute status change directly
      executeStatusChange(occurrenceId, newStatus);
    }
  };

  const executeStatusChange = async (occurrenceId: string, newStatus: string) => {
    try {
      await apiClient.put(`/admin/campaigns/runs/${occurrenceId}/status`, { status: newStatus });
      // Update local state
      setCampaigns(campaigns.map(campaign => ({
        ...campaign,
        runs: campaign.runs.map(run =>
          run.id === occurrenceId ? { ...run, status: newStatus } : run
        ),
      })));
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const confirmPause = async () => {
    if (pendingStatusChange) {
      await executeStatusChange(pendingStatusChange.occurrenceId, pendingStatusChange.newStatus);
    }
    setShowPauseWarning(false);
    setPendingStatusChange(null);
  };

  const openOccurrenceModal = async (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setOccurrenceForm({
      startDate: format(new Date(), 'yyyy-MM-dd'),
      endDate: format(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      numberOfWinners: campaign.numberOfWinners,
      storeIds: [],
    });
    setShowOccurrenceModal(true);
    await loadStores();
  };

  const toggleStoreSelection = (storeId: string) => {
    setOccurrenceForm(prev => ({
      ...prev,
      storeIds: prev.storeIds.includes(storeId)
        ? prev.storeIds.filter(id => id !== storeId)
        : [...prev.storeIds, storeId],
    }));
  };

  const selectAllStores = () => {
    setOccurrenceForm(prev => ({
      ...prev,
      storeIds: stores.map(s => s.id),
    }));
  };

  const deselectAllStores = () => {
    setOccurrenceForm(prev => ({
      ...prev,
      storeIds: [],
    }));
  };

  const openEditModal = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowEditModal(true);
  };

  const handleDeleteCampaign = async (campaign: Campaign) => {
    if (!confirm(`Are you sure you want to delete "${campaign.name}"? This will also delete all occurrences and QR codes.`)) {
      return;
    }

    try {
      await apiClient.delete(`/admin/campaigns/${campaign.id}`);
      loadCampaigns();
    } catch (error: any) {
      console.error('Failed to delete campaign:', error);
      alert(error.message || 'Failed to delete campaign');
    }
  };

  const handleArchiveClick = (campaign: Campaign) => {
    // Check if any occurrences are active
    const hasActiveOccurrences = campaign.runs.some(r => r.status === 'ACTIVE');
    if (hasActiveOccurrences) {
      alert('Cannot archive campaign with active occurrences. Please pause or complete all active occurrences first.');
      return;
    }
    setPendingArchive(campaign);
    setShowArchiveWarning(true);
  };

  const confirmArchive = async () => {
    if (!pendingArchive) return;

    try {
      await apiClient.post(`/admin/campaigns/${pendingArchive.id}/archive`);
      loadCampaigns();
    } catch (error: any) {
      console.error('Failed to archive campaign:', error);
      alert(error.response?.data?.error || 'Failed to archive campaign');
    } finally {
      setShowArchiveWarning(false);
      setPendingArchive(null);
    }
  };

  const handleUnarchive = async (campaign: Campaign) => {
    try {
      await apiClient.post(`/admin/campaigns/${campaign.id}/unarchive`);
      loadCampaigns();
    } catch (error: any) {
      console.error('Failed to restore campaign:', error);
      alert(error.response?.data?.error || 'Failed to restore campaign');
    }
  };

  const openDeleteOccurrenceModal = (campaignId: string, occurrence: CampaignOccurrence) => {
    setPendingDeleteOccurrence({ campaignId, occurrence });
    setShowDeleteOccurrenceModal(true);
  };

  const handleDeleteOccurrence = async (prune: boolean = false) => {
    if (!pendingDeleteOccurrence) return;

    setDeleting(true);
    try {
      const endpoint = prune
        ? `/admin/campaigns/runs/${pendingDeleteOccurrence.occurrence.id}/prune`
        : `/admin/campaigns/runs/${pendingDeleteOccurrence.occurrence.id}`;

      await apiClient.delete(endpoint);
      loadCampaigns();
      setShowDeleteOccurrenceModal(false);
      setPendingDeleteOccurrence(null);
    } catch (error: any) {
      console.error('Failed to delete occurrence:', error);
      alert(error.response?.data?.error || 'Failed to delete occurrence');
    } finally {
      setDeleting(false);
    }
  };

  const handleImageUpload = async (base64Data: string) => {
    if (!selectedCampaign) return;

    const response = await apiClient.post<{ success: boolean; data: { imageUrl: string } }>(
      `/admin/campaigns/${selectedCampaign.id}/image`,
      { image: base64Data }
    );

    setCampaigns(campaigns.map(c =>
      c.id === selectedCampaign.id
        ? { ...c, imageUrl: response.data.data.imageUrl }
        : c
    ));

    setSelectedCampaign({ ...selectedCampaign, imageUrl: response.data.data.imageUrl });
  };

  const handleImageDelete = async () => {
    if (!selectedCampaign) return;

    await apiClient.delete(`/admin/campaigns/${selectedCampaign.id}/image`);

    setCampaigns(campaigns.map(c =>
      c.id === selectedCampaign.id
        ? { ...c, imageUrl: null }
        : c
    ));

    setSelectedCampaign({ ...selectedCampaign, imageUrl: null });
  };

  if (loading) {
    return <div className="animate-pulse">Loading...</div>;
  }

  // Filter campaigns based on active tab
  const activeCampaigns = campaigns.filter(c => !c.isArchived);
  const archivedCampaigns = campaigns.filter(c => c.isArchived);
  const displayedCampaigns = activeTab === 'campaigns' ? activeCampaigns : archivedCampaigns;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Campaigns</h1>
        {activeTab === 'campaigns' && (
          <button
            onClick={() => { setError(null); setShowCreateModal(true); }}
            className="btn-primary"
          >
            Create Campaign
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('campaigns')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'campaigns'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          Campaigns ({activeCampaigns.length})
        </button>
        <button
          onClick={() => setActiveTab('archive')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'archive'
              ? 'border-primary-600 text-primary-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
        >
          <Archive className="w-4 h-4" />
          Archive ({archivedCampaigns.length})
        </button>
      </div>

      <div className="space-y-4">
        {displayedCampaigns.map((campaign) => (
          <div key={campaign.id} className="card">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => toggleExpand(campaign.id)}
                    className="text-gray-400 hover:text-gray-600 p-1"
                  >
                    {expandedCampaigns.has(campaign.id) ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </button>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{campaign.name}</h3>
                    {campaign.description && (
                      <p className="text-sm text-gray-500 mt-1">{campaign.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-2 ml-9 text-sm text-gray-500">
                  <span>{campaign._count.runs} occurrence{campaign._count.runs !== 1 ? 's' : ''}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {campaign.imageUrl && (
                  <div className="w-8 h-8 rounded bg-gray-100 overflow-hidden mr-2">
                    <img src={campaign.imageUrl} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                {!viewAsStoreId && !campaign.isArchived && (
                  <>
                    <button
                      onClick={() => openOccurrenceModal(campaign)}
                      className="btn-secondary text-sm"
                    >
                      + New Occurrence
                    </button>
                    <button
                      onClick={() => navigate(`/admin/campaigns/${campaign.id}/settings`)}
                      className="text-gray-500 hover:text-gray-700 p-2"
                      title="Campaign Settings"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => openEditModal(campaign)}
                      className="text-gray-600 hover:text-gray-900 text-sm px-3 py-1"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleArchiveClick(campaign)}
                      className="text-gray-500 hover:text-gray-700 p-2"
                      title="Archive Campaign"
                    >
                      <Archive className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCampaign(campaign)}
                      className="text-red-500 hover:text-red-700 p-2"
                      title="Delete Campaign"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
                {!viewAsStoreId && campaign.isArchived && (
                  <>
                    <button
                      onClick={() => handleUnarchive(campaign)}
                      className="btn-secondary text-sm flex items-center gap-2"
                    >
                      <ArchiveRestore className="w-4 h-4" />
                      Restore
                    </button>
                    <button
                      onClick={() => handleDeleteCampaign(campaign)}
                      className="text-red-500 hover:text-red-700 p-2"
                      title="Delete Campaign"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {expandedCampaigns.has(campaign.id) && campaign.runs.length > 0 && (
              <div className="mt-4 ml-9 border-t pt-4">
                <table className="min-w-full">
                  <thead>
                    <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                      <th className="pb-2 w-10">Status</th>
                      <th className="pb-2">Occurrence</th>
                      <th className="pb-2">Dates</th>
                      <th className="pb-2">Stores</th>
                      <th className="pb-2">Winners</th>
                      <th className="pb-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {campaign.runs.map((occurrence) => (
                      <tr key={occurrence.id}>
                        <td className="py-2">
                          {!viewAsStoreId ? (
                            <StatusButton
                              occurrence={occurrence}
                              onStatusChange={handleStatusChange}
                            />
                          ) : (
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              occurrence.status === 'ACTIVE' ? 'bg-green-100' :
                              occurrence.status === 'PAUSED' ? 'bg-orange-100' : 'bg-gray-100'
                            }`}>
                              {occurrence.status === 'PAUSED' ? (
                                <Pause className={`w-4 h-4 ${
                                  occurrence.status === 'PAUSED' ? 'text-orange-600' : 'text-gray-400'
                                }`} fill="currentColor" />
                              ) : (
                                <Play className={`w-4 h-4 ${
                                  occurrence.status === 'ACTIVE' ? 'text-green-600' : 'text-gray-400'
                                }`} fill={occurrence.status === 'ACTIVE' ? 'currentColor' : 'none'} />
                              )}
                            </div>
                          )}
                        </td>
                        <td className="py-2 font-medium">#{occurrence.instanceNumber}</td>
                        <td className="py-2 text-sm text-gray-500">
                          {format(new Date(occurrence.startDate), 'MMM d, yyyy')} - {format(new Date(occurrence.endDate), 'MMM d, yyyy')}
                        </td>
                        <td className="py-2 text-sm text-gray-500">
                          {occurrence._count?.participations || 0}
                        </td>
                        <td className="py-2 text-sm text-gray-500">
                          {occurrence.numberOfWinners || campaign.numberOfWinners}
                        </td>
                        <td className="py-2 text-sm">
                          <button
                            onClick={() => navigate(`/admin/campaigns/${campaign.id}/runs/${occurrence.id}${viewAsStoreId ? `?viewAs=${viewAsStoreId}` : ''}`)}
                            className="text-primary-600 hover:text-primary-900 mr-3"
                          >
                            View
                          </button>
                          {!viewAsStoreId && (
                            <>
                              <button
                                onClick={() => navigate(`/admin/campaigns/${campaign.id}/settings`)}
                                className="text-gray-600 hover:text-gray-900 mr-3"
                              >
                                Settings
                              </button>
                              <button
                                onClick={() => openDeleteOccurrenceModal(campaign.id, occurrence)}
                                className="text-red-500 hover:text-red-700"
                                title="Delete Occurrence"
                              >
                                <Trash2 className="w-4 h-4 inline" />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {expandedCampaigns.has(campaign.id) && campaign.runs.length === 0 && (
              <div className="mt-4 ml-9 border-t pt-4 text-center text-gray-500 py-4">
                No occurrences yet. Create an occurrence to start this campaign.
              </div>
            )}
          </div>
        ))}

        {displayedCampaigns.length === 0 && (
          <div className="card text-center py-12 text-gray-500">
            {activeTab === 'campaigns' ? (
              campaigns.length === 0 ? (
                'No campaigns yet. Create your first campaign to get started.'
              ) : (
                'No active campaigns. All campaigns have been archived.'
              )
            ) : (
              <>
                <Archive className="w-8 h-8 mx-auto mb-3 text-gray-300" />
                <p>No archived campaigns.</p>
                <p className="text-sm mt-1">Campaigns you archive will appear here.</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* Pause Warning Modal */}
      {showPauseWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Pause Occurrence?</h2>
            </div>
            <p className="text-gray-600 mb-6">
              All participating store QR codes will be de-activated. Customers scanning the QR codes will see a message that the campaign is paused.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPauseWarning(false);
                  setPendingStatusChange(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmPause}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                Pause Occurrence
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Campaign Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Create Campaign Template</h2>
            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="label">Campaign Name</label>
                <input
                  type="text"
                  value={campaignForm.name}
                  onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
                  className="input"
                  placeholder="e.g., Spring Discovery Event"
                />
              </div>
              <div>
                <label className="label">Description</label>
                <textarea
                  value={campaignForm.description}
                  onChange={(e) => setCampaignForm({ ...campaignForm, description: e.target.value })}
                  className="input"
                  rows={3}
                  placeholder="Describe the campaign..."
                />
              </div>
              <div>
                <label className="label">Default Number of Winners</label>
                <input
                  type="number"
                  min={1}
                  value={campaignForm.numberOfWinners}
                  onChange={(e) => setCampaignForm({ ...campaignForm, numberOfWinners: parseInt(e.target.value) })}
                  className="input"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCampaign}
                disabled={saving || !campaignForm.name}
                className="btn-primary"
              >
                {saving ? 'Creating...' : 'Create Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Occurrence Modal */}
      {showOccurrenceModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create New Occurrence</h2>
            <p className="text-gray-600 mb-4">for {selectedCampaign.name}</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Start Date</label>
                  <input
                    type="date"
                    value={occurrenceForm.startDate}
                    onChange={(e) => setOccurrenceForm({ ...occurrenceForm, startDate: e.target.value })}
                    className="input"
                  />
                </div>
                <div>
                  <label className="label">End Date</label>
                  <input
                    type="date"
                    value={occurrenceForm.endDate}
                    onChange={(e) => setOccurrenceForm({ ...occurrenceForm, endDate: e.target.value })}
                    className="input"
                  />
                </div>
              </div>
              <div>
                <label className="label">Number of Winners</label>
                <input
                  type="number"
                  min={1}
                  value={occurrenceForm.numberOfWinners}
                  onChange={(e) => setOccurrenceForm({ ...occurrenceForm, numberOfWinners: parseInt(e.target.value) })}
                  className="input"
                />
              </div>

              {/* Store Selection */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="label mb-0">Participating Stores</label>
                  <div className="flex gap-2 text-sm">
                    <button
                      type="button"
                      onClick={selectAllStores}
                      className="text-primary-600 hover:text-primary-700"
                    >
                      Select All
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={deselectAllStores}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
                <div className="border rounded-lg max-h-48 overflow-y-auto">
                  {stores.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 text-sm">
                      No active stores available
                    </div>
                  ) : (
                    stores.map((store) => (
                      <label
                        key={store.id}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                      >
                        <input
                          type="checkbox"
                          checked={occurrenceForm.storeIds.includes(store.id)}
                          onChange={() => toggleStoreSelection(store.id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-gray-900">{store.name}</span>
                      </label>
                    ))
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {occurrenceForm.storeIds.length} store{occurrenceForm.storeIds.length !== 1 ? 's' : ''} selected - QR codes will be auto-generated
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowOccurrenceModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOccurrence}
                disabled={saving || !occurrenceForm.startDate || !occurrenceForm.endDate || occurrenceForm.storeIds.length === 0}
                className="btn-primary"
              >
                {saving ? 'Creating...' : 'Create Occurrence'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Campaign Modal */}
      {showEditModal && selectedCampaign && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-bold text-gray-900">Edit Campaign</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Campaign Image */}
            <ImageUpload
              currentImageUrl={selectedCampaign.imageUrl}
              onUpload={handleImageUpload}
              onDelete={handleImageDelete}
              label="Campaign Image"
              aspectRatio="landscape"
              className="mb-6"
            />

            {/* Campaign Info */}
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">Name</label>
                <p className="text-lg font-semibold text-gray-900">{selectedCampaign.name}</p>
              </div>
              {selectedCampaign.description && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Description</label>
                  <p className="text-gray-700">{selectedCampaign.description}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-500">Default Winners</label>
                  <p className="text-2xl font-bold text-gray-900">{selectedCampaign.numberOfWinners}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Total Occurrences</label>
                  <p className="text-2xl font-bold text-gray-900">{selectedCampaign._count.runs}</p>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Warning Modal */}
      {showArchiveWarning && pendingArchive && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <Archive className="w-5 h-5 text-gray-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Archive Campaign?</h2>
            </div>
            <p className="text-gray-600 mb-4">
              <span className="font-semibold">"{pendingArchive.name}"</span> will be moved to the Archive tab.
              You can restore it at any time.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Archived campaigns won't show in the main campaigns list but all data is preserved.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowArchiveWarning(false);
                  setPendingArchive(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmArchive}
                className="btn-primary flex items-center gap-2"
              >
                <Archive className="w-4 h-4" />
                Archive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Occurrence Modal */}
      {showDeleteOccurrenceModal && pendingDeleteOccurrence && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Delete Occurrence #{pendingDeleteOccurrence.occurrence.instanceNumber}?</h2>
            </div>

            <p className="text-gray-600 mb-4">
              Choose how you want to delete this occurrence:
            </p>

            <div className="space-y-3 mb-6">
              {/* Option 1: Soft Delete */}
              <div className="border rounded-lg p-4 hover:border-primary-300 cursor-pointer transition-colors">
                <button
                  onClick={() => handleDeleteOccurrence(false)}
                  disabled={deleting}
                  className="w-full text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full border-2 border-primary-600 flex items-center justify-center mt-0.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-primary-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">Delete (Keep Data)</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Remove from view but preserve all scans, winners, and statistics for historical reporting.
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Option 2: Hard Delete */}
              <div className="border border-red-200 rounded-lg p-4 hover:border-red-400 cursor-pointer transition-colors bg-red-50">
                <button
                  onClick={() => handleDeleteOccurrence(true)}
                  disabled={deleting}
                  className="w-full text-left"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full border-2 border-red-600 flex items-center justify-center mt-0.5">
                    </div>
                    <div>
                      <p className="font-semibold text-red-700">Delete & Prune All Data</p>
                      <p className="text-sm text-red-600 mt-1">
                        Permanently delete occurrence and ALL related data including scans, winners, QR codes, and entries. This cannot be undone.
                      </p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => {
                  setShowDeleteOccurrenceModal(false);
                  setPendingDeleteOccurrence(null);
                }}
                disabled={deleting}
                className="btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
